import React, { useMemo, useEffect, useState } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import {
	MULTI_GROUP_CF_TYPE,
	GROUP_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { CellWrapper } from '../../../common/cell-wrapper/index.tsx';
import type { TransformedGroupField, Group } from '../../../common/field-types/group/types.tsx';
import type { FieldCellProps, BasicListViewItem } from '../../../common/types.tsx';
import { GroupEdit } from './edit/async.tsx';
import { GroupRead } from './read/index.tsx';

export const GroupCell = ({
	rowData: issue,
	cellData: field,
	column: { width: preferredWidth },
	editMode,
	setEditMode,
	cellRef,
	inputValue,
	onInputChange,
}: FieldCellProps<BasicListViewItem, TransformedGroupField>) => {
	const [selectedGroups, setSelectedGroups] = useState<Group[] | Group | null>(
		field?.value ?? null,
	);

	if (fg('jwm_list_new_copy_paste_behaviour')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [{ value: fieldValue }] = useEditField<Group[] | Group | null, null>({
			fieldKey: field?.fieldId ?? '',
			issueKey: issue.key,
			fieldType: field?.type ?? '',
			initialValue: field?.value ?? null,
		});

		if (!editMode && fieldValue !== selectedGroups) {
			setSelectedGroups(fieldValue);
		}
	}

	// Updated the value when a new updated is receive it
	useEffect(() => {
		setSelectedGroups(field?.value ?? null);
	}, [field?.value]);

	const isEditable = useMemo(
		() => issue.fieldConfig?.isEditable || false,
		[issue.fieldConfig?.isEditable],
	);

	const isMulti = useMemo(
		() => field?.type !== 'com.atlassian.jira.plugin.system.customfieldtypes:grouppicker',
		[field?.type],
	);

	return (
		<CellWrapper isEditable={isEditable} testId="business-fields.ui.group.cell.cell-wrapper">
			{editMode ? (
				<GroupEdit
					cellRef={cellRef}
					setEditMode={setEditMode}
					inputValue={inputValue}
					onInputChange={onInputChange}
					isMulti={isMulti}
					fieldName={field?.name || ''}
					onChange={setSelectedGroups}
					preferredWidth={preferredWidth}
					selectedGroups={selectedGroups}
					fieldKey={field?.fieldId || ''}
					issueKey={issue.key}
					fieldType={field?.type || ''}
					initialValue={field?.value ?? null}
					readContent={<GroupRead isEditable={isEditable} selectedGroups={selectedGroups} />}
				/>
			) : (
				<GroupRead isEditable={isEditable} selectedGroups={selectedGroups} />
			)}
		</CellWrapper>
	);
};

export const groupCellHandler = {
	[GROUP_CF_TYPE]: GroupCell,
	[MULTI_GROUP_CF_TYPE]: GroupCell,
};
